// AuthContext.js
import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Consistent endpoint usage
  const verifyAuth = async (token) => {
    try {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await api.get('/v1/api/me/auth-verify');
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Helper function to fetch and add trustScore to user object
  const fetchUserWithTrustScore = async (userData) => {
    if (!userData) return userData;
    
    try {
      // If trustScore is already in user data, return as-is
      if (userData.trustScore) {
        return userData;
      }
      
      // Otherwise, fetch trustScore separately
      const response = await api.get('/v1/api/trust-score/me');
      return {
        ...userData,
        trustScore: response.data
      };
    } catch (error) {
      console.error('Error fetching trust score:', error);
      // Return user without trustScore if fetch fails
      return userData;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const userData = await verifyAuth(token);
        const userWithTrustScore = await fetchUserWithTrustScore(userData.user);
        setUser(userWithTrustScore);
      } catch (error) {
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials) => {
    try {
      setLoading(true);

      const { data } = await api.post('/v1/api/auth/login', credentials);

      if (!data?.user?.requiresTwofactorAuth) {
        localStorage.setItem('token', data.acessToken);
        const userWithTrustScore = await fetchUserWithTrustScore(data.user);
        setUser(userWithTrustScore);
        // New session: reset any one-per-session prompts
        try { sessionStorage.removeItem('profile_incomplete_prompt_shown'); } catch (_) {}
      }

      return data.user;
    } catch (error) {
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleOtpLogin = async (id, otpCode) => {
    setLoading(true);
    try {
      const { data } = await api.post('/v1/api/auth/verify-2fa', {
        userId: id,
        code: otpCode
      });
      localStorage.setItem('token', data.acessToken);
      const userWithTrustScore = await fetchUserWithTrustScore(data.user);
      setUser(userWithTrustScore);
      // New session after OTP: reset any one-per-session prompts
      try { sessionStorage.removeItem('profile_incomplete_prompt_shown'); } catch (_) {}
    } catch (err) {
      console.error('otp auth failed', err)
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loginAdmin = async (credentials) => {
    try {
      setLoading(true);

      const { data } = await api.post('/v1/api/admin/auth/login', credentials);
      localStorage.setItem('token', data.acessToken);

      // const userData = await verifyAuth(data.acessToken); 
      const userWithTrustScore = await fetchUserWithTrustScore(data.user);
      setUser(userWithTrustScore);
      // New session: reset any one-per-session prompts
      try { sessionStorage.removeItem('profile_incomplete_prompt_shown'); } catch (_) {}
      return data.user;
    } catch (error) {
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (newUserData) => {
    setUser(newUserData);
  };

  // Re-fetch authenticated user from backend and update context
  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const data = await verifyAuth(token);
      const userWithTrustScore = await fetchUserWithTrustScore(data.user);
      setUser(userWithTrustScore);
      return userWithTrustScore;
    } catch (err) {
      console.error('Failed to refresh user', err);
      return null;
    }
  };

  const logout = (redirectTo) => {
    // Determine role before clearing user
    const isAdmin = !!user?.isAdmin;
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    // Clear per-session UI flags and transient data
    try {
      sessionStorage.clear();
    } catch (_) {
      try { sessionStorage.removeItem('profile_incomplete_prompt_shown'); } catch (_) {}
    }
    setUser(null);
    // Prefer explicit redirect if provided; otherwise route by role
    const target = redirectTo || (isAdmin ? '/admin/login' : '/login');
    navigate(target);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser, refreshUser, loginAdmin , handleOtpLogin}}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);